import { useCallback, useEffect } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';
import * as rudderanalytics from 'rudder-sdk-js';

import { CompanyDetail } from 'app/api/company/types';
import { ROUTES } from 'app/routes/constants';
import { useModal, useImageHandlers, useAuth, useDeviceType } from 'hooks';
import { AnalyticsLinkType, RudderAnalyticsName, getAnalyticsUserType, useLinkClickedEvenet } from 'rudderAnalytics';

interface UseQuoteCardProps {
  id: number;
  isQuote: boolean;
  isSampleRequest: boolean;
  isDetailsOpened?: boolean;
  currentCycle: number;
  seller?: CompanyDetail;
  onCloseDetails?: () => void;
  onOpenDetails?: (id: string) => void;
}

export const useQuoteCard = ({
  id,
  isQuote,
  isSampleRequest,
  isDetailsOpened,
  currentCycle,
  seller,
  onCloseDetails,
  onOpenDetails,
}: UseQuoteCardProps) => {
  const { isMobileSize } = useDeviceType();
  const navigate = useNavigate();
  const { open: isModalOpen, handleOpenModal, handleCloseModal } = useModal();
  const { handleImageError } = useImageHandlers();
  const { isBuyer } = useAuth();
  const { handleTrackLinkClickEvent } = useLinkClickedEvenet();

  useEffect(() => {
    if (isModalOpen) {
      const analyticsOptions = {
        quote_id: id,
        user_type: getAnalyticsUserType(isBuyer),
        cycle_number: currentCycle,
        supplier_name: seller?.name ?? '',
        supplier_id: seller?.id ?? '',
        is_sample_request: isSampleRequest,
      };

      rudderanalytics.track(RudderAnalyticsName.QUOTE_REVIEWED, analyticsOptions);
    }
  }, [currentCycle, id, isBuyer, isModalOpen, isSampleRequest, seller?.id, seller?.name]);

  useEffect(() => {
    if (isQuote && isDetailsOpened) handleOpenModal();
  }, [handleOpenModal, isDetailsOpened, isQuote]);

  const handleOpenDetails = useCallback(() => {
    handleCloseModal();
    onOpenDetails?.(id.toString());
  }, [handleCloseModal, id, onOpenDetails]);

  const handleCloseDetails = useCallback(() => {
    handleCloseModal();
    onCloseDetails?.();
  }, [handleCloseModal, onCloseDetails]);

  const handleClick = useCallback(() => {
    if (isQuote) {
      handleOpenDetails();
    } else {
      const path = generatePath(ROUTES.common.order, { id });
      navigate(path);
      handleTrackLinkClickEvent({ path, text: 'View Details', type: AnalyticsLinkType.BUTTON });
    }
  }, [isQuote, handleOpenDetails, id, navigate, handleTrackLinkClickEvent]);

  const handleCardClick = useCallback(() => {
    if (isMobileSize) {
      const quotePath = generatePath(
        isSampleRequest ? ROUTES.common.sampleRequestDetails : ROUTES.common.quoteRequestDetails,
        { id },
      );
      const orderPath = generatePath(ROUTES.common.order, { id });

      navigate(isQuote ? quotePath : orderPath);
    } else {
      handleClick();
    }
  }, [id, isMobileSize, isQuote, isSampleRequest, navigate, handleClick]);

  return {
    isMobileSize,
    isModalOpen,
    handleImageError,
    handleClick,
    handleCloseDetails,
    handleCardClick,
  };
};
